import React, { Component } from 'react'
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    List,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Tree,
    Typography,
    message,
} from 'antd'
import {
    ApiOutlined,
    ArrowRightOutlined,
    CodeOutlined,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    AppstoreAddOutlined,
    FolderAddOutlined,
    LinkOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import {
    getApiRegistryImpact,
    getApiRegistryList,
    getApiModuleList,
    getApiModuleMembership,
    getApiClassificationProgress,
    addApiModuleMembers,
    saveApiRegistry,
    updateApiRegistry,
} from '../../request'

const { Text } = Typography
const { TextArea, Search } = Input

const METHOD_COLOR = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    DELETE: 'red',
    PATCH: 'purple',
}

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((method) => ({
    label: method,
    value: method,
}))

const PROTOCOL_OPTIONS = [
    { label: 'HTTP', value: 'http' },
    { label: 'RPC', value: 'rpc' },
    { label: 'GraphQL', value: 'graphql' },
]

const PUBLIC_OPTIONS = [
    { label: '不公开', value: false },
    { label: '公开', value: true },
]

const GENERIC_PATH_SEGMENTS = new Set(['api', 'openapi', 'rest', 'internal', 'v1', 'v2', 'v3'])

const IMPACT_COLOR = {
    HIGH: 'red',
    MEDIUM: 'orange',
    LOW: 'blue',
}

export default class McpApiRegistry extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        loading: false,
        submitting: false,
        formVisible: false,
        detailVisible: false,
        detailLoading: false,
        togglingPublicId: null,
        editRecord: null,
        detailRecord: null,
        impactDetail: null,
        treeSelectedKey: 'all',
        filterKeyword: '',
        filterMethod: undefined,
        filterPublic: undefined,
        navMode: 'module',
        selectedApiCodes: [],
        moduleMeta: { modules: [], membershipByApi: {}, progress: null },
        moduleModalVisible: false,
        moduleModalPrimary: true,
        moduleSubmitting: false,
        moduleModalTarget: undefined,
    }

    componentDidMount() {
        this.loadList()
        this.loadModuleMeta()
    }

    loadModuleMeta = () => {
        Promise.all([
            getApiModuleList({}).catch(() => []),
            getApiModuleMembership({}).catch(() => []),
            getApiClassificationProgress({}).catch(() => null),
        ]).then(([modules, membership, progress]) => {
            const membershipByApi = {}
            ;(Array.isArray(membership) ? membership : []).forEach((row) => {
                if (!row?.apiCode) return
                if (!membershipByApi[row.apiCode]) membershipByApi[row.apiCode] = []
                membershipByApi[row.apiCode].push(row)
            })
            this.setState({
                moduleMeta: {
                    modules: Array.isArray(modules) ? modules : (modules?.list || []),
                    membershipByApi,
                    progress: progress || null,
                },
            })
        })
    }

    getModuleCodesOfApi = (apiCode) => (this.state.moduleMeta.membershipByApi[apiCode] || []).map((row) => row.moduleCode)

    /** 选中接口里已经归属其他模块的条数，用于加入模块前的影响摘要 */
    countCrossModuleSelected = () => this.state.dataSource
        .filter((item) => this.state.selectedApiCodes.includes(item.apiCode))
        .filter((item) => this.getModuleCodesOfApi(item.apiCode).length > 0)
        .length

    normalizeRecord = (record = {}) => ({
        ...record,
        protocol: (record.protocol || 'http').toLowerCase(),
        method: (record.method || 'POST').toUpperCase(),
        isPublic: !!record.isPublic,
        usedByToolsCount: record.usedByToolsCount ?? 0,
    })

    loadList = () => {
        this.setState({ loading: true })
        getApiRegistryList({})
            .then((res) => {
                const list = Array.isArray(res) ? res : (res?.list || [])
                const dataSource = list
                    .map(this.normalizeRecord)
                    .sort((a, b) => this.toTime(b.updatedAt) - this.toTime(a.updatedAt))
                this.setState({ dataSource })
            })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    toTime = (value) => {
        if (!value) return 0
        const time = new Date(value).getTime()
        return Number.isNaN(time) ? 0 : time
    }

    formatDateTime = (value) => {
        if (!value) return '-'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return value
        const pad = (num) => `${num}`.padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    formatJson = (value) => {
        if (!value || value === '{}') return ''
        try {
            return JSON.stringify(JSON.parse(value), null, 2)
        } catch (error) {
            return value
        }
    }

    buildFormValues = (record) => {
        if (!record) {
            return {
                protocol: 'http',
                method: 'POST',
                isPublic: false,
                timeoutMs: 10000,
            }
        }
        return {
            apiCode: record.apiCode,
            apiName: record.apiName,
            sourceSystem: record.sourceSystem,
            protocol: record.protocol || 'http',
            method: record.method || 'POST',
            path: record.path,
            baseUrl: record.baseUrl,
            timeoutMs: record.timeoutMs,
            owner: record.owner,
            isPublic: !!record.isPublic,
            requestSchema: this.formatJson(record.requestSchema),
            responseSchema: this.formatJson(record.responseSchema),
        }
    }

    hydrateForm = (record) => {
        const form = this.formRef.current
        if (!form) return
        form.resetFields()
        form.setFieldsValue(this.buildFormValues(record))
    }

    parseJsonField = (label, value) => {
        if (!value || !String(value).trim()) return '{}'
        const trimmed = String(value).trim()
        try {
            return JSON.stringify(JSON.parse(trimmed))
        } catch (error) {
            message.error(`${label} 不是合法 JSON`)
            throw error
        }
    }

    openCreate = () => {
        this.setState({ formVisible: true, editRecord: null }, () => {
            this.hydrateForm(null)
        })
    }

    openEdit = (record) => {
        const editRecord = this.normalizeRecord(record)
        this.setState({ formVisible: true, editRecord }, () => {
            this.hydrateForm(editRecord)
        })
    }

    openDetail = (record) => {
        const detailRecord = this.normalizeRecord(record)
        this.setState({
            detailVisible: true,
            detailRecord,
            impactDetail: null,
            detailLoading: true,
        })
        getApiRegistryImpact({ id: detailRecord.id })
            .then((impactDetail) => this.setState({ impactDetail: impactDetail || null }))
            .catch(() => this.setState({ impactDetail: null }))
            .finally(() => this.setState({ detailLoading: false }))
    }

    handleSubmit = (values) => {
        let requestSchema
        let responseSchema
        try {
            requestSchema = this.parseJsonField('请求 Schema', values.requestSchema)
            responseSchema = this.parseJsonField('响应 Schema', values.responseSchema)
        } catch (error) {
            return
        }

        const payload = {
            ...values,
            protocol: values.protocol || 'http',
            method: values.method || 'POST',
            isPublic: !!values.isPublic,
            status: 'enabled',
            requestSchema,
            responseSchema,
        }
        const { editRecord } = this.state
        this.setState({ submitting: true })
        const action = editRecord
            ? updateApiRegistry({ ...payload, id: editRecord.id })
            : saveApiRegistry(payload)

        action
            .then(() => {
                message.success(editRecord ? '接口已更新' : '接口已创建')
                this.setState({ formVisible: false, editRecord: null })
                this.loadList()
            })
            .catch(() => {})
            .finally(() => this.setState({ submitting: false }))
    }

    handleTogglePublic = (record) => {
        const nextIsPublic = !record.isPublic
        this.setState({ togglingPublicId: record.id })
        updateApiRegistry({
            id: record.id,
            isPublic: nextIsPublic,
        })
            .then(() => {
                message.success(nextIsPublic ? '已设为公开' : '已设为不公开')
                this.loadList()
                if (this.state.detailRecord?.id === record.id) {
                    this.setState((prevState) => ({
                        detailRecord: prevState.detailRecord
                            ? { ...prevState.detailRecord, isPublic: nextIsPublic }
                            : prevState.detailRecord,
                    }))
                }
            })
            .catch(() => {})
            .finally(() => this.setState({ togglingPublicId: null }))
    }

    getPathGroup = (path) => {
        const segments = String(path || '')
            .split('/')
            .map((item) => item.trim())
            .filter(Boolean)
        const meaningful = segments.find((segment) => !GENERIC_PATH_SEGMENTS.has(segment.toLowerCase()))
        return meaningful || '其他'
    }

    buildTreeKey = (type, sourceSystem, pathGroup) => {
        if (type === 'source') return `source::${encodeURIComponent(sourceSystem || 'unknown')}`
        if (type === 'group') return `group::${encodeURIComponent(sourceSystem || 'unknown')}::${encodeURIComponent(pathGroup || '其他')}`
        return 'all'
    }

    buildModuleTreeKey = (moduleCode) => `module::${encodeURIComponent(moduleCode)}`

    parseTreeSelection = (treeSelectedKey) => {
        if (!treeSelectedKey || treeSelectedKey === 'all') {
            return { sourceSystem: undefined, pathGroup: undefined }
        }
        if (String(treeSelectedKey).startsWith('module::')) {
            return {
                moduleCode: decodeURIComponent(String(treeSelectedKey).slice('module::'.length)),
                sourceSystem: undefined,
                pathGroup: undefined,
            }
        }
        if (String(treeSelectedKey).startsWith('domain::')) {
            return {
                domainName: decodeURIComponent(String(treeSelectedKey).slice('domain::'.length)),
                sourceSystem: undefined,
                pathGroup: undefined,
            }
        }
        if (treeSelectedKey === 'unclassified') {
            return { unclassified: true, sourceSystem: undefined, pathGroup: undefined }
        }
        const parts = String(treeSelectedKey).split('::')
        if (parts[0] === 'source') {
            return {
                sourceSystem: decodeURIComponent(parts[1] || ''),
                pathGroup: undefined,
            }
        }
        if (parts[0] === 'group') {
            return {
                sourceSystem: decodeURIComponent(parts[1] || ''),
                pathGroup: decodeURIComponent(parts[2] || ''),
            }
        }
        return { sourceSystem: undefined, pathGroup: undefined }
    }

    buildNavigationTree = () => (this.state.navMode === 'source'
        ? this.buildSourceNavigationTree()
        : this.buildModuleNavigationTree())

    /** 来源系统视图：保留按来源系统/路径目录排查和批量清理的能力 */
    buildSourceNavigationTree = () => {
        const grouped = this.state.dataSource.reduce((acc, item) => {
            const sourceSystem = item.sourceSystem || '未归类系统'
            const pathGroup = this.getPathGroup(item.path)
            if (!acc[sourceSystem]) acc[sourceSystem] = { count: 0, groups: {} }
            acc[sourceSystem].count += 1
            acc[sourceSystem].groups[pathGroup] = (acc[sourceSystem].groups[pathGroup] || 0) + 1
            return acc
        }, {})

        return Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .map((sourceSystem) => ({
                key: this.buildTreeKey('source', sourceSystem),
                title: this.renderTreeTitle(sourceSystem, grouped[sourceSystem].count),
                children: Object.keys(grouped[sourceSystem].groups)
                    .sort((a, b) => a.localeCompare(b))
                    .map((pathGroup) => ({
                        key: this.buildTreeKey('group', sourceSystem, pathGroup),
                        title: this.renderTreeTitle(pathGroup, grouped[sourceSystem].groups[pathGroup], true),
                        isLeaf: true,
                    })),
            }))
    }

    /** 模块导航：未归类置顶，其余按业务域分组 */
    buildModuleNavigationTree = () => {
        const { modules, progress } = this.state.moduleMeta
        const grouped = {}
        modules.forEach((module) => {
            const domainName = module.domainName || '未设置业务域'
            if (!grouped[domainName]) grouped[domainName] = []
            grouped[domainName].push(module)
        })
        const tree = [{
            key: 'unclassified',
            title: this.renderTreeTitle('未归类', progress?.unclassifiedCount || 0),
            isLeaf: true,
        }]
        Object.keys(grouped).sort((a, b) => a.localeCompare(b)).forEach((domainName) => {
            const list = grouped[domainName]
            tree.push({
                key: `domain::${encodeURIComponent(domainName)}`,
                title: this.renderTreeTitle(domainName, list.length),
                children: list
                    .sort((a, b) => String(a.moduleCode).localeCompare(String(b.moduleCode)))
                    .map((module) => ({
                        key: this.buildModuleTreeKey(module.moduleCode),
                        title: this.renderTreeTitle(
                            `${module.moduleName}${module.status === 'active' ? '' : `（${module.status === 'draft' ? '草稿' : '停用'}）`}`,
                            module.apiCount || 0,
                            true,
                        ),
                        isLeaf: true,
                    })),
            })
        })
        return tree
    }

    getFilteredData = () => {
        const {
            dataSource,
            treeSelectedKey,
            filterKeyword,
            filterMethod,
            filterPublic,
        } = this.state
        const keyword = (filterKeyword || '').trim().toLowerCase()
        const { sourceSystem, pathGroup, moduleCode, unclassified, domainName } = this.parseTreeSelection(treeSelectedKey)
        return dataSource.filter((item) => {
            const moduleCodes = this.getModuleCodesOfApi(item.apiCode)
            if (unclassified && moduleCodes.length) return false
            if (moduleCode && !moduleCodes.includes(moduleCode)) return false
            if (domainName) {
                const rows = this.state.moduleMeta.membershipByApi[item.apiCode] || []
                if (!rows.some((row) => row.domainName === domainName)) return false
            }
            if (sourceSystem && item.sourceSystem !== sourceSystem) return false
            if (pathGroup && this.getPathGroup(item.path) !== pathGroup) return false
            if (filterMethod && item.method !== filterMethod) return false
            if (typeof filterPublic === 'boolean' && !!item.isPublic !== filterPublic) return false
            if (!keyword) return true
            return [
                item.apiCode,
                item.apiName,
                item.path,
                item.baseUrl,
                item.sourceSystem,
                item.owner,
            ].some((field) => (field || '').toLowerCase().includes(keyword))
        })
    }

    clearFilters = () => {
        this.setState({
            treeSelectedKey: 'all',
            filterKeyword: '',
            filterMethod: undefined,
            filterPublic: undefined,
        })
    }

    goToToolWorkbench = (apiCode) => {
        this.props.history.push(`/admin/mcptool?apiCode=${encodeURIComponent(apiCode)}`)
    }

    renderPublicTag = (isPublic) => (
        <Tag color={isPublic ? 'geekblue' : 'default'}>
            {isPublic ? '公开' : '不公开'}
        </Tag>
    )

    renderTreeTitle = (label, count, compact) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minWidth: 0 }}>
            <span style={{
                color: compact ? '#334155' : '#0f172a',
                fontWeight: compact ? 500 : 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {label}
            </span>
            <Tag color={compact ? 'default' : 'processing'} style={{ marginRight: 0 }}>
                {count}
            </Tag>
        </div>
    )

    getCurrentScopeLabel = () => {
        const { sourceSystem, pathGroup, moduleCode, unclassified, domainName } = this.parseTreeSelection(this.state.treeSelectedKey)
        if (unclassified) return '未归类'
        if (domainName) return domainName
        if (moduleCode) {
            const module = this.state.moduleMeta.modules.find((item) => item.moduleCode === moduleCode)
            return module ? `${module.domainName} / ${module.moduleName}` : moduleCode
        }
        if (sourceSystem && pathGroup) return `${sourceSystem} / ${pathGroup}`
        if (sourceSystem) return sourceSystem
        return '全部接口'
    }

    // ---------------- 批量加入模块 ----------------
    openModuleModal = () => {
        this.setState({ moduleModalVisible: true, moduleModalPrimary: true })
    }

    submitModuleModal = (moduleCode) => {
        if (!moduleCode) {
            message.warning('请先选择目标模块')
            return
        }
        const apiCodes = this.state.selectedApiCodes
        if (!apiCodes.length) {
            message.warning('请先勾选接口')
            return
        }
        this.setState({ moduleSubmitting: true })
        addApiModuleMembers({ moduleCode, apiCodes, primary: this.state.moduleModalPrimary })
            .then((res) => {
                const parts = [`新增 ${res?.addedCount || 0} 条`]
                if (res?.referenceCount) parts.push(`其中引用 ${res.referenceCount} 条`)
                if (res?.downgradedCount) parts.push(`${res.downgradedCount} 条已有主模块，按引用加入`)
                if (res?.skippedCount) parts.push(`跳过 ${res.skippedCount} 条`)
                message.success(parts.join('，'))
                this.setState({ moduleSubmitting: false, moduleModalVisible: false, selectedApiCodes: [] })
                this.loadList()
                this.loadModuleMeta()
            })
            .catch((error) => {
                this.setState({ moduleSubmitting: false })
                message.error((error && (error.message || error.msg)) || '加入模块失败')
            })
    }

    bulkSetPublic = (isPublic) => {
        const rows = this.state.dataSource.filter((item) => this.state.selectedApiCodes.includes(item.apiCode))
        const targets = rows.filter((item) => !!item.isPublic !== isPublic)
        if (!targets.length) {
            message.info('所选接口的访问范围已经是目标值')
            return
        }
        Modal.confirm({
            title: `把选中的 ${targets.length} 条接口设为${isPublic ? '公开' : '不公开'}？`,
            content: '接口设为公开后，才能被应用授权给使用者。',
            okText: '确认',
            cancelText: '取消',
            onOk: () => Promise.all(targets.map((item) => updateApiRegistry({ id: item.id, isPublic })))
                .then(() => {
                    message.success('访问范围已更新')
                    this.setState({ selectedApiCodes: [] })
                    this.loadList()
                })
                .catch(() => message.error('更新失败')),
        })
    }

    renderHero = (filteredData) => {
        const total = filteredData.length
        const publicCount = filteredData.filter((item) => item.isPublic).length
        const linkedCount = filteredData.filter((item) => item.usedByToolsCount > 0).length
        const schemaCount = filteredData.filter((item) => item.requestSchema || item.responseSchema).length

        const cards = [
            { title: '接口总数', value: total, color: '#1951d1', icon: <ApiOutlined /> },
            { title: '公开接口', value: publicCount, color: '#2f9e44', icon: <LinkOutlined /> },
            { title: '已补充 Schema', value: schemaCount, color: '#d97706', icon: <CodeOutlined /> },
            { title: '已关联工具', value: linkedCount, color: '#0f9c95', icon: <LinkOutlined /> },
        ]

        return (
            <div style={{ marginBottom: 16 }}>
                <div style={{
                    padding: '24px 26px',
                    borderRadius: 20,
                    border: '1px solid #dce6ff',
                    background: 'linear-gradient(135deg, #f7fbff 0%, #edf4ff 52%, #f2fffc 100%)',
                    marginBottom: 16,
                }}>
                    <Row gutter={[20, 16]} align="middle" justify="space-between">
                        <Col flex="auto">
                            <div style={{ color: '#1951d1', fontWeight: 700, fontSize: 13, letterSpacing: 0.6, marginBottom: 8 }}>
                                Interface Management
                            </div>
                            <div style={{ fontSize: 28, lineHeight: 1.3, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
                                api 管理
                            </div>
                            <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.8 }}>
                                统一维护接口元数据、访问范围、请求方式与 Schema。
                            </Text>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                ghost
                                icon={<AppstoreAddOutlined />}
                                onClick={() => this.props.history.push('/admin/mcpModule')}
                            >
                                模块管理
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Row gutter={[12, 12]}>
                    {cards.map((card) => (
                        <Col xs={24} sm={12} xl={6} key={card.title}>
                            <Card
                                bordered={false}
                                style={{
                                    borderRadius: 18,
                                    border: `1px solid ${card.color}1f`,
                                    background: `linear-gradient(180deg, ${card.color}12 0%, #ffffff 68%)`,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div style={{
                                        width: 46,
                                        height: 46,
                                        borderRadius: 14,
                                        background: `${card.color}14`,
                                        color: card.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 20,
                                    }}>
                                        {card.icon}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{card.title}</div>
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>{card.value}</div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        )
    }

    renderToolbar = () => {
        const {
            filterKeyword,
            filterMethod,
            filterPublic,
        } = this.state

        return (
            <div style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                padding: 14,
                background: '#f8fafc',
                borderRadius: 16,
                border: '1px solid #eef2f7',
                marginBottom: 16,
            }}>
                <Search
                    allowClear
                    value={filterKeyword}
                    placeholder="搜索接口名称、编码、路径、来源系统、负责人"
                    style={{ width: 320 }}
                    onChange={(e) => this.setState({ filterKeyword: e.target.value })}
                />
                <Select
                    allowClear
                    value={filterMethod}
                    placeholder="请求方式"
                    style={{ width: 140 }}
                    options={METHOD_OPTIONS.map((item) => ({
                        ...item,
                        label: <Tag color={METHOD_COLOR[item.value] || 'default'}>{item.label}</Tag>,
                    }))}
                    onChange={(value) => this.setState({ filterMethod: value })}
                />
                <Select
                    allowClear
                    value={filterPublic}
                    placeholder="访问范围"
                    style={{ width: 170 }}
                    options={PUBLIC_OPTIONS}
                    onChange={(value) => this.setState({ filterPublic: value })}
                />
                <Button onClick={this.clearFilters}>清空筛选</Button>
                <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
            </div>
        )
    }

    renderNavigation = () => {
        const treeData = this.buildNavigationTree()
        const defaultExpandedKeys = []
        return (
            <Card
                bordered={false}
                style={{ borderRadius: 20, border: '1px solid #edf1f7', height: '100%' }}
                title={(
                    <Space>
                        <LinkOutlined style={{ color: '#1951d1' }} />
                        <span style={{ fontWeight: 600 }}>接口导航</span>
                    </Space>
                )}
                bodyStyle={{ padding: 14 }}
                extra={(
                    <Space>
                        <Radio.Group
                            size="small"
                            value={this.state.navMode}
                            onChange={(event) => this.setState({ navMode: event.target.value, treeSelectedKey: 'all' })}
                        >
                            <Radio.Button value="module">按模块</Radio.Button>
                            <Radio.Button value="source">按来源系统</Radio.Button>
                        </Radio.Group>
                        <Button
                            type={this.state.treeSelectedKey === 'all' ? 'primary' : 'default'}
                            size="small"
                            onClick={() => this.setState({ treeSelectedKey: 'all' })}
                        >
                            全部接口
                        </Button>
                    </Space>
                )}
            >
                <div style={{
                    marginBottom: 12,
                    padding: '10px 12px',
                    borderRadius: 12,
                    background: '#f8fafc',
                    border: '1px solid #eef2f7',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <div>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>当前范围</div>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{this.getCurrentScopeLabel()}</div>
                    </div>
                </div>
                {treeData.length > 0 ? (
                    <Tree
                        blockNode
                        defaultExpandedKeys={defaultExpandedKeys}
                        selectedKeys={this.state.treeSelectedKey === 'all' ? [] : [this.state.treeSelectedKey]}
                        treeData={treeData}
                        onSelect={(selectedKeys) => {
                            this.setState({ treeSelectedKey: selectedKeys[0] || 'all' })
                        }}
                    />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可导航的接口分类" />
                )}
            </Card>
        )
    }

    buildColumns = () => [
        {
            title: '接口信息',
            key: 'apiInfo',
            width: 320,
            render: (_, record) => (
                <div>
                    <Space size={8} wrap style={{ marginBottom: 8 }}>
                        <Text strong style={{ color: '#0f172a', fontSize: 14 }}>{record.apiName || '-'}</Text>
                        <Text code style={{ fontSize: 11 }}>{record.apiCode || '-'}</Text>
                    </Space>
                    <Space size={8} wrap>
                        {record.sourceSystem ? <Tag color="processing">{record.sourceSystem}</Tag> : null}
                        {this.renderPublicTag(record.isPublic)}
                        {record.owner ? <Tag>{record.owner}</Tag> : <Text type="secondary">未设置负责人</Text>}
                    </Space>
                </div>
            ),
        },
        {
            title: '调用定义',
            key: 'requestInfo',
            width: 360,
            render: (_, record) => (
                <div>
                    <Space size={8} wrap style={{ marginBottom: 8 }}>
                        <Tag color={METHOD_COLOR[record.method] || 'default'} style={{ fontWeight: 700 }}>
                            {record.method}
                        </Tag>
                        <Tag>{(record.protocol || 'http').toUpperCase()}</Tag>
                    </Space>
                    <div style={{ marginBottom: 6 }}>
                        <Text code copyable style={{ fontSize: 12 }}>{record.path || '-'}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.baseUrl || '未配置 Base URL'}
                    </Text>
                </div>
            ),
        },
        {
            title: '配置完成度',
            key: 'completion',
            width: 220,
            render: (_, record) => (
                <div>
                    <Space direction="vertical" size={6}>
                        <Badge status={record.requestSchema ? 'processing' : 'default'} text="请求 Schema" />
                        <Badge status={record.responseSchema ? 'success' : 'default'} text="响应 Schema" />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            关联工具 {record.usedByToolsCount || 0} 个
                        </Text>
                    </Space>
                </div>
            ),
        },
        {
            title: '当前模块',
            key: 'modules',
            width: 200,
            render: (_, record) => {
                const rows = this.state.moduleMeta.membershipByApi[record.apiCode] || []
                if (!rows.length) {
                    return <Tag>未归类</Tag>
                }
                return (
                    <Space size={4} wrap>
                        {rows.map((row) => (
                            <Tag key={row.moduleCode} color={row.isPrimary ? 'blue' : 'default'}>
                                {row.moduleName || row.moduleCode}
                            </Tag>
                        ))}
                    </Space>
                )
            },
        },
        {
            title: '最近更新',
            key: 'updatedAt',
            width: 180,
            render: (_, record) => (
                <div>
                    <div style={{ color: '#111827', fontWeight: 600, marginBottom: 4 }}>
                        {this.formatDateTime(record.updatedAt)}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        超时 {record.timeoutMs || 0} ms
                    </Text>
                </div>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 240,
            fixed: 'right',
            render: (_, record) => (
                <Space wrap size={6}>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => this.openDetail(record)}>
                        详情
                    </Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => this.openEdit(record)}>
                        编辑
                    </Button>
                    <Tooltip
                        title={record.isPublic
                            ? '当前为公开接口，点击后改为不公开，避免被公开场景直接使用。'
                            : '当前为不公开接口，点击后改为公开，允许公开场景直接使用。'}
                    >
                        <Button
                            size="small"
                            type={record.isPublic ? 'default' : 'primary'}
                            ghost={!record.isPublic}
                            loading={this.state.togglingPublicId === record.id}
                            onClick={() => this.handleTogglePublic(record)}
                        >
                            {record.isPublic ? '不公开' : '公开'}
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ]

    renderSchemaCard = (title, value, color) => (
        <Card
            size="small"
            bordered={false}
            title={(
                <Space>
                    <Tag color={color}>{title}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>JSON Schema</Text>
                </Space>
            )}
            style={{ borderRadius: 16, border: '1px solid #edf1f7' }}
        >
            {value ? (
                <pre style={{
                    margin: 0,
                    padding: 16,
                    background: '#0f172a',
                    color: '#e2e8f0',
                    borderRadius: 14,
                    fontSize: 12,
                    lineHeight: 1.7,
                    overflow: 'auto',
                    maxHeight: 320,
                }}>
                    {this.formatJson(value)}
                </pre>
            ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`暂无${title}`} />
            )}
        </Card>
    )

    renderImpact = () => {
        const { detailLoading, detailRecord, impactDetail } = this.state
        if (detailLoading) {
            return (
                <div style={{ padding: '28px 0', textAlign: 'center' }}>
                    <Spin />
                </div>
            )
        }

        const affectedTools = impactDetail?.affectedTools || []
        if (!detailRecord?.usedByToolsCount) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前接口尚未关联 MCP 工具" />
        }
        if (!affectedTools.length) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前没有返回额外影响明细" />
        }

        return (
            <List
                dataSource={affectedTools}
                renderItem={(item) => {
                    const impactLevel = (item.impactLevel || '').toUpperCase()
                    return (
                        <List.Item
                            actions={[
                                <Button
                                    key="config-tool"
                                    size="small"
                                    type="primary"
                                    ghost
                                    onClick={() => this.props.history.push(`/admin/mcptool?toolId=${encodeURIComponent(item.toolId)}`)}
                                >
                                    查看工具
                                </Button>,
                            ]}
                        >
                            <List.Item.Meta
                                title={(
                                    <Space wrap>
                                        <Text strong>{item.toolName || item.toolCode}</Text>
                                        {item.toolCode ? <Text code>{item.toolCode}</Text> : null}
                                        {impactLevel ? (
                                            <Tag color={IMPACT_COLOR[impactLevel] || 'default'}>{impactLevel}</Tag>
                                        ) : null}
                                    </Space>
                                )}
                                description={(
                                    <Space wrap>
                                        {(item.failedMappings || []).length > 0
                                            ? item.failedMappings.map((mapping) => (
                                                <Tag key={`${item.toolId}-${mapping}`} color="orange">{mapping}</Tag>
                                            ))
                                            : <Text type="secondary">暂无失败映射项</Text>}
                                    </Space>
                                )}
                            />
                        </List.Item>
                    )
                }}
            />
        )
    }

    renderForm = () => {
        const { formVisible, editRecord, submitting } = this.state
        return (
            <Modal
                visible={formVisible}
                forceRender
                width={860}
                footer={null}
                onCancel={() => this.setState({ formVisible: false, editRecord: null })}
                title={(
                    <Space>
                        {editRecord ? <EditOutlined /> : <PlusOutlined />}
                        <span>{editRecord ? '编辑接口' : '新建接口'}</span>
                    </Space>
                )}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 12 }}
                    message="接口管理只维护真实接口元数据"
                    description="接口语义包装、示例、映射和发布动作统一放在 MCP 工具管理中处理，这里只聚焦接口本身。"
                />
                <Form
                    ref={this.formRef}
                    layout="vertical"
                    onFinish={this.handleSubmit}
                    initialValues={{
                        protocol: 'http',
                        method: 'POST',
                        isPublic: false,
                        timeoutMs: 10000,
                    }}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                        <Form.Item name="apiCode" label="接口编码" rules={[{ required: true, message: '请输入接口编码' }]}>
                            <Input placeholder="例如 sales.queryDaily" disabled={!!editRecord} />
                        </Form.Item>
                        <Form.Item name="apiName" label="接口名称" rules={[{ required: true, message: '请输入接口名称' }]}>
                            <Input placeholder="例如 查询销售日报" />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '180px 140px 1fr', gap: '0 16px' }}>
                        <Form.Item name="sourceSystem" label="来源系统" rules={[{ required: true, message: '请输入来源系统' }]}>
                            <Input placeholder="例如 lechun-bi" />
                        </Form.Item>
                        <Form.Item name="protocol" label="协议">
                            <Select options={PROTOCOL_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="baseUrl" label="Base URL" rules={[{ required: true, message: '请输入 Base URL' }]}>
                            <Input placeholder="例如 https://scmapi.lechun.cc/lechun-bi" />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 160px 180px', gap: '0 16px' }}>
                        <Form.Item name="method" label="请求方式" rules={[{ required: true, message: '请选择请求方式' }]}>
                            <Select options={METHOD_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="path" label="接口路径" rules={[{ required: true, message: '请输入接口路径' }]}>
                            <Input placeholder="/api/v1/sales/daily" />
                        </Form.Item>
                        <Form.Item name="timeoutMs" label="超时时间(ms)">
                            <InputNumber style={{ width: '100%' }} min={500} max={60000} step={500} />
                        </Form.Item>
                        <Form.Item name="isPublic" label="访问范围">
                            <Select options={PUBLIC_OPTIONS} />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0 16px' }}>
                        <Form.Item name="owner" label="负责人">
                            <Input placeholder="例如 张三" />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                        <Form.Item
                            name="requestSchema"
                            label="请求 Schema"
                            extra="这里维护的参数会作为「调用参数」写进使用者的提示词；required 里的字段标为必填"
                        >
                            <TextArea
                                rows={10}
                                placeholder='{"type":"object","properties":{"date":{"type":"string"}}}'
                                style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="responseSchema"
                            label="响应 Schema"
                            extra="这里维护的字段会作为「关键返回字段」写进使用者的提示词"
                        >
                            <TextArea
                                rows={10}
                                placeholder='{"type":"object","properties":{"data":{"type":"array"}}}'
                                style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 12 }}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => this.setState({ formVisible: false, editRecord: null })}>取消</Button>
                            <Button type="primary" htmlType="submit" loading={submitting}>
                                {editRecord ? '保存修改' : '创建接口'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        )
    }

    renderDetail = () => {
        const { detailVisible, detailRecord } = this.state
        return (
            <Drawer
                width={820}
                visible={detailVisible}
                onClose={() => this.setState({ detailVisible: false, detailRecord: null, impactDetail: null })}
                bodyStyle={{ padding: 20, background: '#f5f7fb' }}
                title={(
                    <Space>
                        <ApiOutlined />
                        <span>{detailRecord?.apiName || '接口详情'}</span>
                    </Space>
                )}
            >
                {detailRecord ? (
                    <div>
                        <Card
                            bordered={false}
                            style={{ borderRadius: 18, border: '1px solid #edf1f7', marginBottom: 16 }}
                        >
                            <Descriptions column={2} size="small" colon={false}>
                                <Descriptions.Item label="接口编码">{detailRecord.apiCode || '-'}</Descriptions.Item>
                                <Descriptions.Item label="接口名称">{detailRecord.apiName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="来源系统">{detailRecord.sourceSystem || '-'}</Descriptions.Item>
                                <Descriptions.Item label="负责人">{detailRecord.owner || '-'}</Descriptions.Item>
                                <Descriptions.Item label="访问范围">{this.renderPublicTag(detailRecord.isPublic)}</Descriptions.Item>
                                <Descriptions.Item label="请求方式">
                                    <Space size={8}>
                                        <Tag color={METHOD_COLOR[detailRecord.method] || 'default'}>{detailRecord.method}</Tag>
                                        <Tag>{(detailRecord.protocol || 'http').toUpperCase()}</Tag>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="接口路径">
                                    <Text code>{detailRecord.path || '-'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Base URL">
                                    <Text copyable>{detailRecord.baseUrl || '-'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="超时时间">{detailRecord.timeoutMs || 0} ms</Descriptions.Item>
                                <Descriptions.Item label="更新时间">{this.formatDateTime(detailRecord.updatedAt)}</Descriptions.Item>
                                <Descriptions.Item label="关联工具">{detailRecord.usedByToolsCount || 0} 个</Descriptions.Item>
                            </Descriptions>
                            <div style={{ marginTop: 16 }}>
                                <Space>
                                    <Button icon={<EditOutlined />} onClick={() => this.openEdit(detailRecord)}>编辑接口</Button>
                                    <Button type="primary" ghost icon={<ArrowRightOutlined />} onClick={() => this.goToToolWorkbench(detailRecord.apiCode)}>
                                        去配置工具
                                    </Button>
                                </Space>
                            </div>
                        </Card>

                        {detailRecord.usedByToolsCount > 0 ? (
                            <Card
                                bordered={false}
                                style={{ borderRadius: 18, border: '1px solid #edf1f7', marginBottom: 16 }}
                                title="工具关联"
                            >
                                {this.renderImpact()}
                            </Card>
                        ) : null}

                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                {this.renderSchemaCard('请求 Schema', detailRecord.requestSchema, 'processing')}
                            </Col>
                            <Col span={24}>
                                {this.renderSchemaCard('响应 Schema', detailRecord.responseSchema, 'success')}
                            </Col>
                        </Row>
                    </div>
                ) : null}
            </Drawer>
        )
    }

    renderModuleModal = () => {
        const { moduleModalVisible, moduleSubmitting, moduleModalPrimary, selectedApiCodes, moduleMeta } = this.state
        const options = (moduleMeta.modules || []).map((module) => ({
            label: `${module.domainName ? `${module.domainName} / ` : ''}${module.moduleName}` +
                `${module.status === 'active' ? '' : '（未在用）'}`,
            value: module.moduleCode,
            disabled: module.status !== 'active',
        }))
        return (
            <Modal
                title={`加入模块（已选 ${selectedApiCodes.length} 条接口）`}
                visible={moduleModalVisible}
                confirmLoading={moduleSubmitting}
                okText="确认加入"
                cancelText="取消"
                onOk={() => this.submitModuleModal(this.state.moduleModalTarget)}
                onCancel={() => this.setState({ moduleModalVisible: false })}
                width={560}
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 6 }}>目标模块</div>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="选择要归入的功能模块"
                        optionFilterProp="label"
                        options={options}
                        value={this.state.moduleModalTarget}
                        onChange={(value) => this.setState({ moduleModalTarget: value })}
                    />
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                        草稿和停用的模块不能加入，需要先在功能模块页把权限来源补齐并设为在用。
                    </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 6 }}>归属方式</div>
                    <Radio.Group
                        value={moduleModalPrimary}
                        onChange={(e) => this.setState({ moduleModalPrimary: e.target.value })}
                    >
                        <Radio.Button value>主模块</Radio.Button>
                        <Radio.Button value={false}>引用</Radio.Button>
                    </Radio.Group>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                        一个接口只有一个主模块；已经属于其他模块的接口会自动以引用方式加入，不会从原模块移除。
                    </div>
                </div>
                <div>
                    <div style={{ marginBottom: 6 }}>将加入的接口</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                        共 {selectedApiCodes.length} 条；
                        其中 {this.countCrossModuleSelected()} 条已属于其他模块，将以引用方式加入；
                        新增接口默认不公开，需要在接口管理页单独设为公开后应用才能授权。
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
                        {this.state.dataSource
                            .filter((item) => selectedApiCodes.includes(item.apiCode))
                            .map((item) => (
                                <div key={item.apiCode} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 6px' }}>
                                    <span>{item.apiName || item.apiCode}</span>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{item.sourceSystem}</Text>
                                </div>
                            ))}
                    </div>
                </div>
            </Modal>
        )
    }

    render() {
        const filteredData = this.getFilteredData()
        return (
            <div style={{ padding: '0 4px' }}>
                {this.renderHero(filteredData)}

                <Row gutter={[16, 16]} align="stretch">
                    <Col xs={24} xl={6}>
                        {this.renderNavigation()}
                    </Col>
                    <Col xs={24} xl={18}>
                        <Card
                            bordered={false}
                            style={{ borderRadius: 20, border: '1px solid #edf1f7' }}
                            title={(
                                <Space>
                                    <ApiOutlined style={{ color: '#1951d1' }} />
                                    <span style={{ fontWeight: 600 }}>接口管理</span>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        当前范围：{this.getCurrentScopeLabel()}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        共 {filteredData.length} 条接口记录
                                    </Text>
                                </Space>
                            )}
                            extra={(
                                <Space>
                                    {this.parseTreeSelection(this.state.treeSelectedKey).sourceSystem && (
                                        <Button 
                                            danger 
                                            icon={<DeleteOutlined />} 
                                            onClick={() => this.handleBatchDelete(this.parseTreeSelection(this.state.treeSelectedKey).sourceSystem)}
                                        >
                                            清空当前系统接口
                                        </Button>
                                    )}
                                    <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>新建接口</Button>
                                </Space>
                            )}
                        >
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 16, borderRadius: 12 }}
                                message="接口按功能模块归类，模块是应用授权的单位"
                                description="左侧导航按业务域和模块聚合，未归类置顶；勾选接口后可批量加入模块，模块下的公开接口才会授权给应用使用者。"
                            />
                            {this.renderToolbar()}
                            {this.state.selectedApiCodes.length > 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 10,
                                    alignItems: 'center',
                                    padding: '10px 14px',
                                    marginBottom: 12,
                                    borderRadius: 12,
                                    background: '#e6f7ff',
                                    border: '1px solid #91d5ff',
                                }}>
                                    <span>已选 <strong>{this.state.selectedApiCodes.length}</strong> 条接口</span>
                                    <Button type="primary" size="small" icon={<FolderAddOutlined />} onClick={this.openModuleModal}>
                                        加入模块
                                    </Button>
                                    <Button size="small" onClick={() => this.bulkSetPublic(true)}>设为公开</Button>
                                    <Button size="small" onClick={() => this.bulkSetPublic(false)}>设为不公开</Button>
                                    <Button type="link" size="small" onClick={() => this.setState({ selectedApiCodes: [] })}>
                                        清除选择
                                    </Button>
                                </div>
                            ) : null}
                            <Table
                                bordered
                                size="middle"
                                rowKey="id"
                                loading={this.state.loading}
                                dataSource={filteredData}
                                rowSelection={{
                                    selectedRowKeys: filteredData
                                        .filter((item) => this.state.selectedApiCodes.includes(item.apiCode))
                                        .map((item) => item.id),
                                    onChange: (keys, rows) => this.setState({ selectedApiCodes: rows.map((row) => row.apiCode) }),
                                }}
                                columns={this.buildColumns()}
                                scroll={{ x: 1280 }}
                                pagination={{ size: 'small', pageSize: 20, showSizeChanger: true }}
                                locale={{
                                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前筛选范围内没有接口记录" />,
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                {this.renderForm()}
                {this.renderDetail()}
                {this.renderModuleModal()}
            </div>
        )
    }
}
